import { useState, useEffect } from "react";
import { X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdvertisementBanner({ placement = "homepage", format = "top_bar" }) {
  const [ads, setAds] = useState([]);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    fetchAds();
  }, [placement]);

  const fetchAds = async () => {
    try {
      const response = await fetch(`${API_URL}/api/advertisements?placement=${placement}`);
      if (response.ok) {
        const data = await response.json();
        setAds(data.advertisements.filter(ad => ad.ad_format === format));
      }
    } catch (error) {
      console.error("Error fetching ads:", error);
    }
  };

  const trackImpression = async (adId) => {
    try {
      await fetch(`${API_URL}/api/advertisements/${adId}/impression`, { method: "POST" });
    } catch (error) {
      console.error("Error tracking impression:", error);
    }
  };

  const trackClick = async (adId) => {
    try {
      await fetch(`${API_URL}/api/advertisements/${adId}/click`, { method: "POST" });
    } catch (error) {
      console.error("Error tracking click:", error);
    }
  };

  const handleClick = (ad) => {
    trackClick(ad.ad_id);
    if (ad.redirect_url) {
      window.open(ad.redirect_url, "_blank");
    }
  };

  const handleDismiss = (adId) => {
    setDismissed([...dismissed, adId]);
  };

  useEffect(() => {
    ads.forEach(ad => {
      if (!dismissed.includes(ad.ad_id)) {
        trackImpression(ad.ad_id);
      }
    });
  }, [ads, dismissed]);

  const visibleAds = ads.filter(ad => !dismissed.includes(ad.ad_id));

  if (visibleAds.length === 0) return null;

  // Top Bar Style
  if (format === "top_bar") {
    const ad = visibleAds[0];
    return (
      <div className="bg-gradient-to-r from-violet-600 to-pink-600 text-white py-2 px-4 relative">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-4">
          <span className="text-sm font-medium">{ad.title}</span>
          {ad.description && <span className="text-sm text-white/80 hidden sm:inline">- {ad.description}</span>}
          <button 
            onClick={() => handleClick(ad)}
            className="bg-white text-violet-600 text-xs font-semibold px-3 py-1 rounded-full hover:bg-gray-100 transition"
          >
            Learn More
          </button>
        </div>
        <button 
          onClick={() => handleDismiss(ad.ad_id)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Banner Style
  if (format === "banner") {
    const ad = visibleAds[0];
    return (
      <div className="relative rounded-lg overflow-hidden cursor-pointer group" onClick={() => handleClick(ad)}>
        {ad.image_url ? (
          <div className="banner-photo-container">
            <img src={ad.image_url} alt={ad.title} />
          </div>
        ) : (
          <div className="w-full h-32 bg-gradient-to-r from-violet-500 to-pink-500 flex items-center justify-center">
            <div className="text-center text-white">
              <h3 className="font-bold text-lg">{ad.title}</h3>
              {ad.description && <p className="text-sm text-white/80">{ad.description}</p>}
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition" />
        <span className="absolute top-2 right-2 text-xs bg-black/50 text-white px-2 py-0.5 rounded">Ad</span>
      </div>
    );
  }

  // Sidebar Style
  if (format === "sidebar") {
    return (
      <div className="space-y-3">
        {visibleAds.slice(0, 3).map(ad => (
          <div 
            key={ad.ad_id}
            className="bg-white rounded-lg p-3 border border-gray-100 cursor-pointer hover:shadow-md transition"
            onClick={() => handleClick(ad)}
          >
            {ad.image_url && (
              <div className="banner-photo-container rounded mb-2 overflow-hidden">
                <img src={ad.image_url} alt={ad.title} />
              </div>
            )}
            <h4 className="font-medium text-sm text-gray-900">{ad.title}</h4>
            {ad.description && <p className="text-xs text-gray-500 mt-1">{ad.description}</p>}
            <span className="text-xs text-gray-400 mt-2 block">Sponsored</span>
          </div>
        ))}
      </div>
    );
  }

  // Featured Style
  if (format === "featured") {
    const ad = visibleAds[0];
    return (
      <div 
        className="bg-gradient-to-br from-violet-600 via-purple-600 to-pink-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition"
        onClick={() => handleClick(ad)}
      >
        <span className="text-xs bg-white/20 px-2 py-0.5 rounded">Featured</span>
        <h3 className="font-bold text-xl mt-3">{ad.title}</h3>
        {ad.description && <p className="text-white/80 mt-2">{ad.description}</p>}
        <button className="mt-4 bg-white text-violet-600 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-gray-100 transition">
          Learn More
        </button>
      </div>
    );
  }

  return null;
}
